import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  MapPin,
  Clock,
  // Calendar,
  Plus,
  Search,
  Filter,
  //   Eye,
  Edit2,
  Trash2,
  X,
  Save,
  // CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../ui/utils";
import { useScreenPermission } from "hooks/useScreenPermission";
import {
  createIntercityCheckout,
  deleteIntercityCheckout,
  getIntercityCheckouts,
  getVenues,
  getMiqaats,
  MiqaatApi,
  updateIntercityCheckout,
  VenueApi,
} from "services/intercityService";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface IntercityCheckout {
  id: string;
  tourType: "general" | "miqaat";
  fromVenue: string;
  fromVenueId: number;
  toVenue: string;
  toVenueId: number | null;
  miqaatName?: string;
  checkoutTime: string;
  travelTime?: string;
  startDate: string;
  endDate: string;
  status: "active" | "inactive";
}

interface IntercityCheckoutPageProps {
  onNavigate?: (page: string) => void;
}

export const IntercityCheckoutPage = ({
  onNavigate,
}: IntercityCheckoutPageProps) => {
  const { setPageHeader } = usePageHeader();
  const [checkouts, setCheckouts] = useState<IntercityCheckout[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedCheckout, setSelectedCheckout] =
    useState<IntercityCheckout | null>(null);
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<IntercityCheckout | null>(
    null,
  );

  const [miqaats, setMiqaats] = useState<MiqaatApi[]>([]);
  const [loading, setLoading] = useState(false);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const {
    canAdd,
    canEdit,
    canDelete,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();

  const [formData, setFormData] = useState<Partial<IntercityCheckout>>({
    tourType: "general",
    fromVenue: "",
    toVenue: "",
    miqaatName: "",
    checkoutTime: "",
    travelTime: "",
    startDate: "",
    endDate: "",
    status: "active",
  });

  const [filters, setFilters] = useState({
    tourType: "all",
    venue: "all",
    status: "all",
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        setLoading(true);
        const data = await getVenues();
        setVenuesFromApi(data.filter((v) => v.isActive));
      } catch (e) {
        console.error("Venue fetch failed", e);
      } finally {
        setLoading(false);
      }
    };

    fetchVenues();
  }, []);

  useEffect(() => {
    const fetchCheckouts = async () => {
      try {
        setLoading(true);

        const res = await getIntercityCheckouts({
          pageNo,
          pageSize,
        });

        const mapped: IntercityCheckout[] = res.items.map((item) => ({
          id: item.publicId,
          tourType: item.tourType === 1 ? "general" : "miqaat",
          fromVenue: item.fromVenueName,
          fromVenueId: item.fromVenueId,
          toVenue: item.toVenueName || "",
          toVenueId: item.toVenueId ?? null,
          miqaatName: item.miqaatName || "",
          checkoutTime: item.checkoutTime?.slice(0, 5) || "",
          travelTime: item.travelTime
            ? String(parseInt(item.travelTime.split(":")[0]))
            : "",
          startDate: item.startDate ? item.startDate.split("T")[0] : "",
          endDate: item.endDate ? item.endDate.split("T")[0] : "",
          status: item.isActive ? "active" : "inactive",
        }));

        setCheckouts(mapped);
        setTotalCount(res.totalCount);
      } catch (error) {
        console.error("Failed to fetch intercity checkouts", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckouts();
  }, [pageNo, pageSize]);

  useEffect(() => {
    const fetchMiqaats = async () => {
      try {
        setLoading(true);
        const data = await getMiqaats();
        setMiqaats(data.filter((m) => m.isActive));
      } catch (e) {
        console.error("Miqaat fetch failed", e);
      } finally {
        setLoading(false);
      }
    };

    fetchMiqaats();
  }, []);

  // Filter checkouts
  const filteredCheckouts = checkouts.filter((checkout) => {
    const matchesSearch =
      checkout.fromVenue.toLowerCase().includes(searchTerm.toLowerCase()) ||
      checkout.checkoutTime.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTourType =
      filters.tourType === "all" || checkout.tourType === filters.tourType;
    const matchesVenue =
      filters.venue === "all" || checkout.fromVenue === filters.venue;
    const matchesStatus =
      filters.status === "all" || checkout.status === filters.status;

    return matchesSearch && matchesTourType && matchesVenue && matchesStatus;
  });

  // Statistics
  // const stats = {
  //   total: checkouts.length,
  //   general: checkouts.filter(c => c.tourType === 'general').length,
  //   miqaat: checkouts.filter(c => c.tourType === 'miqaat').length,
  //   active: checkouts.filter(c => c.status === 'active').length,
  // };

  const handleAdd = () => {
    setFormData({
      tourType: "general",
      fromVenue: "",
      toVenue: "",
      miqaatName: "",
      checkoutTime: "",
      travelTime: "",
      startDate: "",
      endDate: "",
      status: "active",
    });
    setSelectedCheckout(null);
    setIsViewMode(false);
    setIsDialogOpen(true);
  };

  //   const handleView = (checkout: IntercityCheckout) => {
  //     setSelectedCheckout(checkout);
  //     setFormData(checkout);
  //     setIsViewMode(true);
  //     setIsDialogOpen(true);
  //   };

  const handleEdit = (checkout: IntercityCheckout) => {
    setSelectedCheckout(checkout);
    setFormData({
      ...checkout,
      toVenue: checkout.toVenue || "",
      miqaatName: checkout.miqaatName || "",
      travelTime: checkout.travelTime
        ? String(parseInt(checkout.travelTime.split(":")[0]))
        : "",
    });
    setIsViewMode(false);
    setIsDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingItem) return;

    try {
      setLoading(true);
      await deleteIntercityCheckout(deletingItem.id);

      toast.success("Intercity checkout deleted successfully");
      const res = await getIntercityCheckouts({
        pageNo,
        pageSize,
      });

      const mapped: IntercityCheckout[] = res.items.map((item) => ({
        id: item.publicId,
        tourType: item.tourType === 1 ? "general" : "miqaat",
        fromVenue: item.fromVenueName,
        fromVenueId: item.fromVenueId,
        toVenue: item.toVenueName || "",
        toVenueId: item.toVenueId ?? null,
        miqaatName: item.miqaatName || "",
        checkoutTime: item.checkoutTime?.slice(0, 5) || "",
        travelTime: item.travelTime
          ? String(parseInt(item.travelTime.split(":")[0]))
          : "",
        startDate: item.startDate ? item.startDate.split("T")[0] : "",
        endDate: item.endDate ? item.endDate.split("T")[0] : "",
        status: item.isActive ? "active" : "inactive",
      }));

      setCheckouts(mapped);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete intercity checkout");
    } finally {
      setLoading(false);
    }

    setIsDeleteDialogOpen(false);
    setDeletingItem(null);
  };

  const getVenueId = (venueName: string) => {
    const venue = venuesFromApi.find((v) => v.venueName === venueName);
    return venue ? venue.venueId : 0;
  };

  const isDuplicateCheckout = () => {
    return checkouts.some((c) => {
      if (selectedCheckout && c.id === selectedCheckout.id) return false;

      return (
        c.tourType === formData.tourType &&
        c.fromVenue === formData.fromVenue &&
        c.checkoutTime === formData.checkoutTime &&
        c.status === "active"
      );
    });
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};

    if (!formData.tourType) newErrors.tourType = "Tour Type is required";
    if (!formData.fromVenue) newErrors.fromVenue = "Venue is required";
    if (!formData.checkoutTime)
      newErrors.checkoutTime = "Checkout time is required";

    if (formData.tourType === "general") {
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.endDate) newErrors.endDate = "End date is required";
    }

    if (!formData.toVenue) newErrors.toVenue = "To Venue is required";
    if (!formData.travelTime) newErrors.travelTime = "Travel time is required";

    if (formData.tourType === "miqaat" && !formData.miqaatName) {
      newErrors.miqaatName = "Miqaat is required";
    }

    if (formData.fromVenue === formData.toVenue) {
      newErrors.toVenue = "From and To venue cannot be same";
    }

    if (isDuplicateCheckout()) {
      setErrors((prev) => ({
        ...prev,
        checkoutTime:
          "Checkout time already used for another venue & tour type",
      }));

      toast.error("Duplicate checkout time not allowed");
      return;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});

    try {
      setLoading(true);
      const payload = {
        tourType: formData.tourType === "general" ? 1 : 2,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        fromVenueId: getVenueId(formData.fromVenue!),
        toVenueId: formData.toVenue ? getVenueId(formData.toVenue) : null,

        checkoutTime: `${formData.checkoutTime}:00`,
        travelTime: formData.travelTime
          ? `${formData.travelTime.padStart(2, "0")}:00:00`
          : null,

        startDate:
          formData.tourType === "general"
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              new Date(formData.startDate!).toISOString()
            : null,

        endDate:
          formData.tourType === "general"
            ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              new Date(formData.endDate!).toISOString()
            : null,

        miqaatId:
          formData.tourType === "miqaat"
            ? miqaats.find((m) => m.miqaatName === formData.miqaatName)
                ?.miqaatId || null
            : null,

        isActive: formData.status === "active",
        createdBy: 1,
      };

      if (selectedCheckout) {
        const updatePayload = {
          tourType: payload.tourType,
          fromVenueId: payload.fromVenueId,
          toVenueId: payload.toVenueId,
          checkoutTime: payload.checkoutTime,
          travelTime: payload.travelTime,
          startDate: payload.startDate,
          endDate: payload.endDate,
          miqaatId: payload.miqaatId,
          isActive: payload.isActive,
          updatedBy: 1,
        };

        await updateIntercityCheckout(selectedCheckout.id, updatePayload);
        toast.success("Intercity checkout updated successfully");
      } else {
        await createIntercityCheckout(payload);

        toast.success("Intercity checkout created successfully");
      }

      const res = await getIntercityCheckouts({
        pageNo,
        pageSize,
      });

      const mapped: IntercityCheckout[] = res.items.map((item) => ({
        id: item.publicId,
        tourType: item.tourType === 1 ? "general" : "miqaat",
        fromVenue: item.fromVenueName,
        fromVenueId: item.fromVenueId,
        toVenue: item.toVenueName || "",
        toVenueId: item.toVenueId ?? null,
        miqaatName: item.miqaatName || "",
        travelTime: item.travelTime
          ? String(parseInt(item.travelTime.split(":")[0]))
          : "",
        checkoutTime: item.checkoutTime?.slice(0, 5) || "",
        startDate: item.startDate ? item.startDate.split("T")[0] : "",
        endDate: item.endDate ? item.endDate.split("T")[0] : "",
        status: item.isActive ? "active" : "inactive",
      }));

      setCheckouts(mapped);

      setIsDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save intercity checkout");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by venue or checkout time..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(1);
                }}
                className="pl-10"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              style={showFilters ? { background: "#5B9BD5" } : {}}
              className={
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }
              title="Toggle Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
            {canAdd && (
              <Button
                onClick={handleAdd}
                className="bg-[#5B9BD5] hover:bg-[#4A8AC4] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Checkout
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-sky-200 bg-sky-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-sky-600">Total Checkouts</p>
                  <p className="text-2xl font-bold text-sky-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">General Tours</p>
                  <p className="text-2xl font-bold text-amber-900">{stats.general}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Miqaat Tours</p>
                  <p className="text-2xl font-bold text-purple-900">{stats.miqaat}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Active</p>
                  <p className="text-2xl font-bold text-green-900">{stats.active}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div> */}

        {/* Filters */}
        {showFilters && (
          <Card
            className="bg-white border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50"
            style={{ gap: "0px" }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle
                    style={{ color: "#5B9BD5" }}
                    className="flex items-center gap-2 text-lg"
                  >
                    <Filter className="w-5 h-5" />
                    Filters
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Filter checkouts by type, venue, and status
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter-type" className="text-sm">
                    Tour Type
                  </Label>
                  <Select
                    value={filters.tourType}
                    onValueChange={(value) =>
                      setFilters({ ...filters, tourType: value })
                    }
                  >
                    <SelectTrigger id="filter-type" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="miqaat">Miqaat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-venue" className="text-sm">
                    Venue
                  </Label>
                  <Select
                    value={filters.venue}
                    onValueChange={(value) =>
                      setFilters({ ...filters, venue: value })
                    }
                  >
                    <SelectTrigger id="filter-venue" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Venues</SelectItem>
                      {venuesFromApi.map((v) => (
                        <SelectItem key={v.venueId} value={v.venueName}>
                          {v.venueName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-between items-center pt-2">
                <p className="text-sm text-gray-600">
                  {filteredCheckouts.length} checkout(s) found
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFilters({ tourType: "all", venue: "all", status: "all" })
                  }
                  className="text-sm"
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Tour Type
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      From Venue
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Checkout Time
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Start Date
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      End Date
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-center"
                    >
                      Status
                    </TableHead>
                    {(canEdit || canDelete) && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCheckouts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-gray-500"
                      >
                        No checkouts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCheckouts.map((checkout) => (
                      <TableRow
                        key={checkout.id}
                        className="hover:bg-sky-50/30"
                      >
                        <TableCell className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs font-medium",
                              checkout.tourType === "general"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : "bg-purple-50 text-purple-700 border-purple-200",
                            )}
                          >
                            {checkout.tourType === "miqaat"
                              ? checkout.miqaatName || "Miqaat"
                              : "General"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="font-medium text-gray-700">
                              {checkout.fromVenue}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="font-mono text-sm font-semibold">
                              {checkout.checkoutTime}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-700">
                          {checkout.tourType === "miqaat" ? (
                            <span className="text-gray-400 italic">
                              Not Applicable
                            </span>
                          ) : (
                            formatDate(checkout.startDate)
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-sm text-gray-700">
                          {checkout.tourType === "miqaat" ? (
                            <span className="text-gray-400 italic">
                              Not Applicable
                            </span>
                          ) : (
                            formatDate(checkout.endDate)
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-center">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs font-medium",
                              checkout.status === "active"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : "bg-gray-50 text-gray-700 border-gray-200",
                            )}
                          >
                            {checkout.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            {/* <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleView(checkout)}
                              className="h-8 w-8 hover:bg-sky-50 text-[#5B9BD5]"
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </Button> */}
                            {canEdit && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => handleEdit(checkout)}
                                className="text-[#5B9BD5] hover:text-[#4A8BC2] hover:bg-sky-50 h-8 w-8 p-0"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                  setDeletingItem(checkout);
                                  setIsDeleteDialogOpen(true);
                                }}
                                className="h-8 w-8 text-red-700 hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={pageNo}
            onPageChange={(page) => setPageNo(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPageNo(1);
            }}
            label="intercity checkouts"
          />
        </Card>

        {/* Add/Edit/View Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-white max-w-2xl" style={{ gap: "0px" }}>
            <DialogHeader>
              <DialogTitle>
                {isViewMode ? "View" : selectedCheckout ? "Edit" : "Add"}{" "}
                Intervenue Checkout
              </DialogTitle>
              <DialogDescription>
                {isViewMode
                  ? "View intervenue checkout details"
                  : selectedCheckout
                    ? "Update intervenue checkout information"
                    : "Create a new intervenue checkout schedule"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Tour Type */}
              <div className="space-y-2">
                <SelectFieldset label="Tour Type" required>
                  <Select
                    value={formData.tourType}
                    onValueChange={(value: "general" | "miqaat") => {
                      setFormData({
                        ...formData,
                        tourType: value,
                        startDate: value === "miqaat" ? "" : formData.startDate,
                        endDate: value === "miqaat" ? "" : formData.endDate,
                        miqaatName:
                          value === "general" ? "" : formData.miqaatName,
                      });
                    }}
                    disabled={isViewMode}
                  >
                    <SelectTriggerUI id="tourType">
                      <SelectValue placeholder="Select tour type" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="miqaat">Miqaat</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.tourType && (
                  <p className="text-xs text-red-500 mt-1">{errors.tourType}</p>
                )}
              </div>

              {formData.tourType === "miqaat" && (
                <div className="space-y-2">
                  <SelectFieldset label="Miqaat Name" required>
                    <Select
                      value={formData.miqaatName}
                      onValueChange={(value) => {
                        setFormData({ ...formData, miqaatName: value });
                        setErrors((prev) => ({ ...prev, miqaatName: "" }));
                      }}
                    >
                      <SelectTriggerUI>
                        <SelectValue placeholder="Select miqaat" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {miqaats.map((m) => (
                          <SelectItem key={m.miqaatId} value={m.miqaatName}>
                            {m.miqaatName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {errors.miqaatName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.miqaatName}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* From Venue */}
                <div className="space-y-2">
                  <SelectFieldset label="From Venue" required>
                    <Select
                      value={formData.fromVenue}
                      onValueChange={(value) => {
                        setFormData({ ...formData, fromVenue: value });
                        setErrors((prev) => ({ ...prev, fromVenue: "" }));
                      }}
                      disabled={isViewMode}
                    >
                      <SelectTriggerUI id="fromVenue">
                        <SelectValue placeholder="Select venue" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {venuesFromApi.map((v) => (
                          <SelectItem key={v.venueId} value={v.venueName}>
                            {v.venueName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {errors.fromVenue && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.fromVenue}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <SelectFieldset label="To Venue" required>
                    <Select
                      value={formData.toVenue}
                      onValueChange={(value) => {
                        setFormData({ ...formData, toVenue: value });
                        setErrors((prev) => ({ ...prev, toVenue: "" }));
                      }}
                    >
                      <SelectTriggerUI>
                        <SelectValue placeholder="Select venue" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {venuesFromApi.map((v) => (
                          <SelectItem key={v.venueId} value={v.venueName}>
                            {v.venueName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                  {errors.toVenue && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.toVenue}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Checkout Time */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Checkout Time"
                    required
                    id="checkoutTime"
                    type="time"
                    value={formData.checkoutTime}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        checkoutTime: e.target.value,
                      });
                      setErrors((prev) => ({ ...prev, checkoutTime: "" }));
                    }}
                  />
                  {errors.checkoutTime && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.checkoutTime}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Travel Time (hrs)"
                    required
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Enter hours"
                    value={formData.travelTime}
                    onChange={(e) => {
                      setFormData({ ...formData, travelTime: e.target.value });
                      setErrors((prev) => ({ ...prev, travelTime: "" }));
                    }}
                  />

                  {errors.travelTime && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.travelTime}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Start Date */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Start Date"
                    required={formData.tourType === "general"}
                    id="startDate"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => {
                      setFormData({ ...formData, startDate: e.target.value });
                      setErrors((prev) => ({ ...prev, startDate: "" }));
                    }}
                    disabled={isViewMode || formData.tourType === "miqaat"}
                    className={
                      formData.tourType === "miqaat"
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }
                  />
                  {errors.startDate && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.startDate}
                    </p>
                  )}
                </div>

                {/* End Date */}
                <div className="space-y-2">
                  <InputFieldset
                    label="End Date"
                    required={formData.tourType === "general"}
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => {
                      setFormData({ ...formData, endDate: e.target.value });
                      setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    disabled={isViewMode || formData.tourType === "miqaat"}
                    className={
                      formData.tourType === "miqaat"
                        ? "bg-gray-100 cursor-not-allowed"
                        : ""
                    }
                  />
                  {errors.endDate && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.endDate}
                    </p>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <SelectFieldset label="Status">
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                    disabled={isViewMode}
                  >
                    <SelectTriggerUI id="status">
                      <SelectValue placeholder="Select status" />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>

              {formData.tourType === "miqaat" && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    For Miqaat tours, start and end dates are not applicable and
                    will remain disabled.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                {isViewMode ? "Close" : "Cancel"}
              </Button>
              {!isViewMode && (
                <Button
                  onClick={handleSave}
                  className="bg-[#5B9BD5] hover:bg-[#4A8AC4] text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="text-red-600">
                Delete Intercity Checkout
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{" "}
                <span className="font-semibold">
                  "{deletingItem?.fromVenue}"
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteDialogOpen(false);
                  setDeletingItem(null);
                }}
              >
                No, Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};
